import React, { useState } from "react"
import { Button } from "@components/Button"
import { Modal } from "@UILibrary/Modal"
import { SearchableSelect } from "@UILibrary/SearchableSelect"
import { api } from "../api"

type WealthManager = { id: number; name: string }

type Props = {
  selectedClientIds: number[]
  // The wealth manager the selection currently belongs to, or null when the
  // selection doesn't share one. Its presence is what opens the modal — there
  // is nothing to choose here, it is stated, not picked.
  fromWealthManager: WealthManager | null
  onClose: () => void
  onSubmit: (toWmId: number) => Promise<void>
  submitting: boolean
}

export const BulkTransferModal: React.FC<Props> = ({
  selectedClientIds,
  fromWealthManager,
  onClose,
  onSubmit,
  submitting,
}) => {
  const [toId, setToId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Remounts the picker on close so it drops the name it displays.
  const [formKey, setFormKey] = useState(0)

  const handleClose = () => {
    setToId(null)
    setError(null)
    setFormKey((k) => k + 1)
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!toId) {
      setError("Pick a destination wealth manager")
      return
    }
    try {
      await onSubmit(toId)
      handleClose()
    } catch (err: any) {
      setError(err?.message || "Bulk transfer failed")
    }
  }

  if (!fromWealthManager) return null

  return (
    <Modal open title="Bulk transfer clients" onClose={handleClose}>
      <form onSubmit={handleSubmit}>
        <p className="fsp-admin__confirm-body" style={{ marginTop: 0 }}>
          Transferring {selectedClientIds.length} {selectedClientIds.length === 1 ? "client" : "clients"} from{" "}
          <strong>{fromWealthManager.name}</strong>.
        </p>

        {/* Server-side type-ahead, same picker the assignment modal and the
            clients filter use. The source is excluded so a client can't be
            transferred to the wealth manager it already belongs to. */}
        <div className="fsp-admin__form-section">
          <label className="fsp-admin__field-label" htmlFor="fsp-transfer-to">To wealth manager</label>
          <SearchableSelect
            key={`to-${formKey}`}
            inputId="fsp-transfer-to"
            queryKey="fsp-admin-transfer-to"
            fetcher={(q) => api.wealthManagers.index(q)}
            value={toId}
            onChange={(id) => setToId(id)}
            placeholder="Search destination wealth manager…"
            excludeIds={[fromWealthManager.id]}
          />
        </div>

        {error && <div className="fsp-admin__notice fsp-admin__notice--error">{error}</div>}

        <div className="fsp-admin__modal-actions">
          <Button secondary type="button" onClick={handleClose}>Cancel</Button>
          <Button primary type="submit" disabled={submitting}>
            {submitting ? "Transferring…" : "Transfer"}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
